import { useState } from "react";
import styled from "@emotion/styled";
import { Flex } from "styles/utility-components";

import {
  convertDocumentFiltersToSearchRequest,
  DEFAULT_DOCUMENT_COLUMNS,
  DEFAULT_DOCUMENT_FILTERS,
  DocumentColumns,
  DocumentFilters,
  FilterSection,
  getDocumentColumnCopy,
} from "components/documents/utils";
import FilterFormResults from "components/filter_form/filter_form_results/FilterFormResults";
import EmptyStateAction from "components/saved_searches/EmptyStateAction";
import FilterPanelToggle from "components/search_page/FilterPanelToggle";
import Button from "components/ui/button/Button";
import EmptyState from "components/ui/empty_state/EmptyState";
import SidebarOld from "components/ui/sidebar/SidebarOld";
import Tabs from "components/ui/tabs/Tabs";
import { appRoutes } from "lib/appRoutes";
import EditColumnsButton from "lib/core_components/EditColumnsButton";
import { createUseDebounce } from "lib/debounce";
import { useDocuments, UseSearchDocuments } from "lib/hooks/api/documents/useDocuments";
import { useCurrentUser } from "lib/hooks/api/users/useCurrentUser";
import { useTablePageSize } from "lib/hooks/useTablePageSize";
import { EventDataTypes } from "lib/tracking";
import { ColumnSetting } from "lib/types/models";
import DocumentBuyingSignalsResults from "./DocumentBuyingSignalsResults";
import { DocumentTable } from "./DocumentTable";
import { FilterForm } from "./FilterForm";
import { useSavedViews } from "./useDocumentsPageState";

import css from "./FilterableDocuments.module.scss";

import DocumentSvg from "../../../assets/images/document.svg";

type FilterableDocumentProps = {
  defaultColumns?: ColumnSetting<DocumentColumns>[];
  defaultFilters?: DocumentFilters;
  hiddenFilters?: (keyof DocumentFilters)[];
  hiddenSections?: FilterSection[];
  emptyState?: JSX.Element;
  showBanner?: boolean;
  disablePreview?: boolean;
  documentProvider?: UseSearchDocuments;
  showBuyingSignalsTab?: boolean;
};

type DocumentsResultsTabKey = "documents" | "signals";

export function FilterableDocuments({
  defaultColumns = DEFAULT_DOCUMENT_COLUMNS,
  defaultFilters = DEFAULT_DOCUMENT_FILTERS,
  hiddenFilters,
  hiddenSections,
  emptyState,
  showBanner,
  disablePreview,
  documentProvider = useDocuments,
  showBuyingSignalsTab = false,
}: FilterableDocumentProps) {
  const { data: currentUser } = useCurrentUser();
  const [isSidebarOpen, setIsSidebarOpen] = useState(true);
  const [activeResultsTab, setActiveResultsTab] = useState<DocumentsResultsTabKey>("documents");
  const [tableColumns, setTableColumns] = useState<ColumnSetting<DocumentColumns>[] | undefined>(
    undefined,
  );
  const [activeGroups, setActiveGroups] = useState<string[]>([]);

  const {
    filters,
    setFilters,
    setSelectedView,
    getAppliedFiltersCount,
    showNoView,
    recentViews,
    selectedView,
    viewOrCustomFiltersApplied,
  } = useSavedViews(defaultFilters);

  function buildAvailableColumns() {
    return defaultColumns;
  }

  const availableColumns = buildAvailableColumns();

  const appliedColumns = tableColumns ?? availableColumns;

  const useDebounce = createUseDebounce(500);
  const debouncedSetFilters = useDebounce((filters: DocumentFilters) => {
    setFilters(filters);
  });

  const appliedFiltersCount = getAppliedFiltersCount(filters);

  const emptyStateMessage = {
    title: recentViews?.length > 1 ? "Recent searches" : "Apply filters to get started",
    description:
      recentViews?.length > 1
        ? undefined
        : "Find key documents from annual reports and budgets to strategy docs, FOI requests and meeting minutes.",
  };

  const [pageSize] = useTablePageSize("document-table");
  const { data, isLoading } = documentProvider(
    convertDocumentFiltersToSearchRequest(filters, currentUser, { current: 1, pageSize }),
    {
      enabled: viewOrCustomFiltersApplied,
    },
  );

  const documentsResultsContent =
    appliedFiltersCount > 0 || !showNoView ? (
      <Flex column gap={16} width={"100%"}>
        <Flex justifyContent="space-between">
          <FilterPanelToggle
            open={isSidebarOpen}
            onOpenChange={setIsSidebarOpen}
            appliedFiltersCount={appliedFiltersCount}
          />
          <EditColumnsButton
            allAvailableColumns={availableColumns}
            selectedColumns={appliedColumns}
            onNewColumnSettingChange={(c) =>
              setTableColumns([...(c as ColumnSetting<DocumentColumns>[])])
            }
            dataType={EventDataTypes.documents}
          />
        </Flex>
        <FilterFormResults
          totalResults={data?.totalResults || 0}
          sortedByField={getDocumentColumnCopy(
            filters.sort?.field || DEFAULT_DOCUMENT_FILTERS.sort.field,
          )}
          isLoading={isLoading}
        />
        <DocumentTable
          filters={filters}
          onSortChange={(sort) => setFilters({ ...filters, sort })}
          columnSettings={appliedColumns.map((c) => c.field)}
          emptyText={
            <EmptyState
              type="noResults"
              heading="Empty, but full of potential!"
              description="Try changing your settings below to get back on track"
              size="large"
              width="100%"
              action={
                <Button
                  as="button"
                  buttonType="primary"
                  icon="filterFunnel02"
                  onClick={() => {
                    setIsSidebarOpen(true);
                  }}
                >
                  Filters
                </Button>
              }
            />
          }
          showBanner={showBanner}
          disablePreview={disablePreview}
          documentProvider={documentProvider}
          defaultFilters={defaultFilters}
        />
      </Flex>
    ) : (
      <EmptyState
        type="startingState"
        heading={emptyStateMessage.title}
        description={emptyStateMessage.description}
        size="large"
        width="100%"
        action={
          <EmptyStateAction
            hydratedRecentViews={recentViews}
            setSelectedView={setSelectedView}
            dataType={"document"}
          />
        }
      />
    );

  return (
    <div className={css.container}>
      {emptyState ? (
        emptyState
      ) : (
        <TableContentArea>
          <SidebarOld open={isSidebarOpen} offsetTop={73}>
            <FilterForm
              resetKey={selectedView?.id}
              onChange={debouncedSetFilters}
              filters={filters}
              options={{
                hiddenFilters,
                disabledSections: hiddenSections,
              }}
              activeGroups={activeGroups}
              onActiveGroupsChange={setActiveGroups}
              onClose={() => setIsSidebarOpen(false)}
              isEmptyState={!viewOrCustomFiltersApplied}
              defaultFilters={defaultFilters}
              onSideBarOpenChange={setIsSidebarOpen}
              isSidebar
            />
          </SidebarOld>
          <TableMainArea>
            {showBuyingSignalsTab ? (
              <Tabs
                fullWidth
                value={activeResultsTab}
                onChange={(key) => setActiveResultsTab(key as DocumentsResultsTabKey)}
                items={[
                  {
                    key: "documents",
                    label: "Documents",
                    icon: "file06",
                    children: documentsResultsContent,
                  },
                  {
                    key: "signals",
                    label: "Buying signals",
                    icon: "magicWand",
                    children: (
                      <DocumentBuyingSignalsResults
                        filters={filters}
                        enabled={viewOrCustomFiltersApplied}
                        currentUser={currentUser}
                      />
                    ),
                  },
                ]}
              />
            ) : (
              documentsResultsContent
            )}
          </TableMainArea>
        </TableContentArea>
      )}
    </div>
  );
}

export default function UncollectedDocuments() {
  return (
    <div className={css.uncollectedState}>
      <img src={DocumentSvg} alt="document" />
      <h3>We don't have this buyer's documents yet</h3>
      <p>We’re working hard to collect documents for all buyer types.</p>
      <Button as="a" {...appRoutes.buyersAll}>
        Explore other buyers
      </Button>
    </div>
  );
}

const TableContentArea = styled(Flex)({
  flexDirection: "row",
  width: "100%",
  height: "100%",
  padding: "0",
});

const TableMainArea = styled(Flex)({
  flexGrow: 1,
  flexShrink: 1,
  flexBasis: 0,
  minWidth: "0",
  width: "100%",
  justifyContent: "center",
  padding: "16px 24px",
});
